import { applyColorPalette } from 'modules/Editor/store/infographSlice.helpers';
import { InfographState } from 'types/infographTypes';
import { generatePaletteFromColor } from 'utils/color';

import { CardTemplateData } from '../DesignGenerator.types';
import { replaceIcons } from '../helpers/iconReplace';
import { replaceImages } from '../helpers/imageReplace';
import {
  BASE_TEMPLATE_IDS,
  DEFAULT_COLOR_PALETTES,
  SHUFFLE_COLORS_PERCENT,
  SKIP_COLOR_SHUFFLE_TEMPLATES,
} from './CardGenerator.config';

/**
 * Randomly select one of the base templates
 * If the randomly selected template is the `ignoreTemplateId` will
 * return the next template in the list.
 *
 * @param ignoreTemplateId
 * @returns
 */
const randomlySelectBaseTemplate = (ignoreTemplateId?: string) => {
  let baseTemplateIdx = Math.floor(Math.random() * BASE_TEMPLATE_IDS.length);
  let selectedTemplate = BASE_TEMPLATE_IDS[baseTemplateIdx] || BASE_TEMPLATE_IDS[0];

  // If randomly selected the ignored template, select the next one
  if (ignoreTemplateId && selectedTemplate.e2DesignId === ignoreTemplateId) {
    baseTemplateIdx = (baseTemplateIdx + 1) % BASE_TEMPLATE_IDS.length;
    selectedTemplate = BASE_TEMPLATE_IDS[baseTemplateIdx] || BASE_TEMPLATE_IDS[0];
  }
  return selectedTemplate;
};

/**
 * Randomly select a color palette from the default color palettes
 * If a baseColor is provided, generate a new palette with that color's tints + shades
 *
 * @param baseColor
 * @returns string[] array of colors
 */
const getColorPalette = (baseColor?: string): string[] => {
  if (baseColor) {
    return generatePaletteFromColor(baseColor);
  }

  const paletteIdx = Math.floor(Math.random() * DEFAULT_COLOR_PALETTES.length);
  return DEFAULT_COLOR_PALETTES[paletteIdx] || DEFAULT_COLOR_PALETTES[0];
};

type ProcessCardTemplateArgs = {
  // Template data taken from config
  baseTemplateData: CardTemplateData;

  // Infograph that was processed by backend
  generatedInfograph: InfographState;

  // Keywords obtained from OpenAi - used for icon/image search and replace
  keywords?: string[];
};

/**
 * Processes a generated infograph for card templates.
 * Processing has 3 steps:
 * 1. Replace icons - as defined by replaceables
 * 2. Apply a randomly selected colour palette
 * 3. Replace images (TODO)
 *
 * @param templateId base template id
 * @param generatedInfograph
 * @param baseColor
 * @returns updated infograph
 */
const processCardTemplate = async ({
  baseTemplateData: { e2DesignId: templateId, baseColor, replaceables },
  generatedInfograph,
  keywords,
}: ProcessCardTemplateArgs): Promise<InfographState> => {
  // Step 1: Replace Icons
  let updatedInfograph = generatedInfograph;
  if (replaceables?.icons?.length && keywords?.length) {
    updatedInfograph = await replaceIcons(updatedInfograph, replaceables.icons, keywords);
  }

  if (replaceables?.images?.length && keywords?.length) {
    updatedInfograph = await replaceImages(updatedInfograph, replaceables.images, keywords);
  }

  // Step 2: Apply color palette
  // Only perform colour shuffle a certain percentage of the time defined by SHUFFLE_COLORS_PERCENT
  const shouldKeepOriginalColors = Math.round(Math.random() * 100) % 100 >= SHUFFLE_COLORS_PERCENT;
  if (shouldKeepOriginalColors || SKIP_COLOR_SHUFFLE_TEMPLATES.includes(templateId)) {
    return updatedInfograph;
  }

  // Card templates always have one page only so always get first page
  const pageId = generatedInfograph.pageOrder[0];
  updatedInfograph = applyColorPalette(updatedInfograph, [pageId], getColorPalette(baseColor));

  return updatedInfograph;
};

export { randomlySelectBaseTemplate, processCardTemplate };
